<template>
  <div class="invoice-detail-page">
    <CommonPageShell
      :title="headerTitle"
      :actions="headerActions"
      @action="onHeaderAction"
    >
      <template #left>
        <div class="invoice-detail-header-left">
          <AppBreadcrumb
            mode="invoice-detail"
            context-key="app.breadcrumb.invoice-detail"
            :context-id="invoiceBreadcrumbContextId"
            :reference="invoiceSerialFolioReference"
            :labels="invoiceBreadcrumbLabels"
            :aria-label="invoiceBreadcrumbAriaLabel"
            :show-back="true"
            :back-label="invoiceBreadcrumbBackLabel"
            :fallback-target="{ name: 'invoices' }"
          />

          <div class="app-detail-header invoice-detail-heading-wrap">
            <div class="app-detail-heading invoice-detail-heading-content">
              <div class="invoice-detail-heading-main">
                <h2 class="invoice-detail-heading-title">{{ invoiceHeaderTitle }}</h2>
                <span
                  class="app-status-pill app-detail-status-pill"
                  :class="invoiceIsCanceled ? 'app-status-inactive' : 'app-status-active'"
                >
                  {{ invoiceStatusLabel }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #actions="{ onAction }">
        <div class="app-page-header-actions app-detail-header-actions">
          <button
            v-for="action in primaryHeaderActions"
            :key="action.key"
            class="app-header-btn"
            :class="[
              `app-header-btn--${action.variant || 'primary'}`,
              {
                'invoice-detail-header-btn--icon-only': action.iconOnly,
                'invoice-detail-header-btn--download-bundle': action.key === 'download',
              }
            ]"
            type="button"
            :title="String(action?.title || action?.label || '')"
            @click="handlePrimaryHeaderAction(action, onAction)"
          >
            <i v-if="action.icon" class="fa" :class="action.icon" aria-hidden="true"></i>
            <span v-if="!action.iconOnly">{{ action.label }}</span>
          </button>

          <div v-if="secondaryHeaderActions.length" class="app-detail-more-actions-wrap" ref="moreActionsWrap">
            <button
              type="button"
              class="app-header-btn app-header-btn--ghost app-detail-more-actions-btn"
              :title="text.detail.header.moreActionsTitle"
              @click.stop="toggleMoreActionsMenu"
            >
              <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
            </button>

            <div v-if="showMoreActionsMenu" class="app-detail-more-actions-menu">
              <button
                v-for="action in secondaryHeaderActions"
                :key="action.key"
                type="button"
                :class="{ 'invoice-detail-more-actions-menu__item--critical': action.key === 'cancel' }"
                :title="action.key === 'cancel' ? String(text?.detail?.header?.cancelImportantHint || '').trim() : ''"
                @click="handleSecondaryHeaderAction(action, onAction)"
              >
                <span>{{ action.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </template>

      <template #content>
        <section class="invoice-detail-content-wrap">
          <AppSectionTabs
            v-model="activeTab"
            :tabs="invoiceDetailTabs"
            :aria-label="invoiceSectionTabsAriaLabel"
          />

          <InvoiceDetailTabsPanel
            :active-tab="activeTab"
            :invoice="invoiceData"
            :issuer="issuer"
            :selected-receiver="selectedReceiver"
            :invoice-info="invoiceInfo"
            :payment-method-label="paymentMethodLabel"
            :payment-form-label="paymentFormLabel"
            :concepts="concepts"
            :comments="comments"
            :currency="currency"
            :subtotal="subtotal"
            :discount-amount="discountAmount"
            :transferred-tax-amount="transferredTaxAmount"
            :withheld-tax-amount="withheldTaxAmount"
            :total="total"
            :invoice-operation="invoiceOperation"
            :shipping-refresh-tick="invoiceShippingRefreshTick"
            @pay-invoice="openPayInvoiceModal"
          />
        </section>
      </template>
    </CommonPageShell>

    <PayModal
      :show="showPayInvoiceModal"
      :row="payInvoiceRow"
      :submitting="payInvoiceSubmitting"
      @close="closePayInvoiceModal"
      @submit="submitPayInvoiceModal"
    />

    <SendEmailModal
      context-type="invoice"
      :context-data="sendEmailContextData"
      :show="showSendEmailModal"
      :submitting="sendEmailModalSubmitting"
      @close="closeSendEmailModal"
      @submit="submitSendEmailModal"
    />
  </div>
</template>

<script>
import AppBreadcrumb from '@/components/common/AppBreadcrumb.vue';
import SendEmailModal from '@/components/sendEmail/SendEmailModal.vue';
import AppSectionTabs from '@/components/common/AppSectionTabs.vue';
import CommonPageShell from '@/components/common/CommonPageShell.vue';
import InvoiceDetailTabsPanel from '@/components/invoices/detail/InvoiceDetailTabsPanel.vue';
import PayModal from '@/components/pay/modal/PayModal.vue';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  createInvoiceDetailState,
  invoiceDetailComputed,
  invoiceDetailMethods,
} from '@/composables/invoices/useInvoiceDetailPage';

const { catalog: invoiceText } = useDomainText('invoices');
const { catalog: sendEmailText } = useDomainText('sendEmail');

export default {
  name: 'InvoiceDetailPage',
  components: {
    AppBreadcrumb,
    SendEmailModal,
    AppSectionTabs,
    CommonPageShell,
    InvoiceDetailTabsPanel,
    PayModal,
  },
  data() {
    return {
      text: invoiceText,
      sendEmailText,
      activeTab: 'detail',
      ...createInvoiceDetailState(),
    };
  },
  computed: {
    ...invoiceDetailComputed,
    invoiceDetailTabs() {
      const tabLabels = this.text?.detail?.tabs || {};

      return [
        { key: 'detail', label: String(tabLabels.detail || '').trim() },
        { key: 'shipping', label: String(tabLabels.shipping || '').trim() },
        { key: 'documents', label: String(tabLabels.documents || '').trim() },
        { key: 'history', label: String(tabLabels.history || '').trim() },
      ];
    },
    invoiceSectionTabsAriaLabel() {
      return String(this.text?.detail?.sectionTabsAriaLabel || '').trim();
    },
    invoiceBreadcrumbAriaLabel() {
      return String(this.text?.detail?.breadcrumb?.ariaLabel || '').trim();
    },
    invoiceBreadcrumbBackLabel() {
      return String(this.text?.detail?.breadcrumb?.backLabel || '').trim();
    },
    invoiceBreadcrumbLabels() {
      return this.text?.detail?.breadcrumb || {};
    },
    invoiceBreadcrumbContextId() {
      return String(this.invoiceId || '').trim();
    },
    invoiceSerialFolioReference() {
      const serial = String(this.invoiceData?.serial || '').trim();
      const folio = String(this.invoiceData?.folio || '').trim();
      const serialFolio = `${serial}${folio}`.trim();
      return serialFolio !== '' ? serialFolio : String(this.invoiceFolioBadge || '').trim();
    },
    invoiceHeaderTitle() {
      const title = String(this.text?.detail?.page?.title || '').trim();
      const reference = String(this.invoiceSerialFolioReference || '').trim();
      return reference !== '' ? `${title} ${reference}`.trim() : title;
    },
    sendEmailContextData() {
      const invoiceData = this.invoiceData && typeof this.invoiceData === 'object'
        ? this.invoiceData
        : {};

      return {
        id: this.invoiceId,
        total: invoiceData?.total,
        customerName: String(invoiceData?.customer_name || invoiceData?.customerName || '').trim(),
        currency: String(invoiceData?.currency || '').trim(),
        invoiceData,
      };
    },
  },
  created() {
    this.initializeDetailPage();
  },
  beforeUnmount() {
    this.cleanupDetailPage();
  },
  methods: invoiceDetailMethods,
};
</script>

<style scoped>
.invoice-detail-header-left {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.invoice-detail-content-wrap {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.invoice-detail-heading-wrap {
  align-items: center;
}

.invoice-detail-heading-content {
  width: 100%;
}

.invoice-detail-heading-main {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.invoice-detail-heading-title {
  margin: 0;
}

.invoice-detail-more-actions-menu__item--critical {
  color: #b91c1c;
}

.invoice-detail-more-actions-menu__item--critical:hover {
  background: rgba(185, 28, 28, 0.08);
  color: #991b1b;
}

.invoice-detail-header-btn--icon-only {
  min-width: 36px;
  padding-left: 10px;
  padding-right: 10px;
}

.invoice-detail-header-btn--download-bundle {
  background: rgba(31, 42, 61, 0.1);
  color: #1f2a3d;
  border: 1px solid rgba(31, 42, 61, 0.2);
}

.invoice-detail-header-btn--download-bundle:not(:disabled):hover {
  background: rgba(31, 42, 61, 0.16);
}

@media (max-width: 767px) {
  .invoice-detail-header-left {
    gap: 6px;
  }

  .invoice-detail-heading-main {
    gap: 8px;
  }
}
</style>
